import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { LoginService } from "../login.service";
import { SettingsService } from "../settings.service";

interface GroupSelection {
  AutoIndex: number;
  ids: number[];
  isDefault: boolean;
}

@Component({
    selector: "app-multi-select-manager",
    templateUrl: "./multi-select-manager.component.html",
    styleUrls: ["./multi-select-manager.component.scss"],
    standalone: false
})
export class MultiSelectManagerComponent implements OnInit {
  @Output() closed = new EventEmitter();

  selectedGroupIndex = 0;
  groupSelections: GroupSelection[] = [];

  constructor(public loginService: LoginService, private settingsService: SettingsService) {}

  async ngOnInit(): Promise<void> {
    try {
      const groups = await this.settingsService.getMultiGroups();
      this.groupSelections = groups.map((g) => {
        const ids = this.isByRooms
          ? g.RIDs?.split(",").map(Number) || []
          : g.DocUIDs?.split(",").map(Number) || [];

        return {
          AutoIndex: g.AutoIndex,
          ids,
          isDefault: g.isDefault,
        };
      });

      const defaultIndex = this.groupSelections.findIndex((g) => g.isDefault);
      this.selectedGroupIndex = defaultIndex !== -1 ? defaultIndex : 0;
    } catch (error) {
      console.error("Error loading multi select groups:", error);
    }
  }

  get isByRooms(): boolean {
    return this.loginService.premissions?.ApptsByRoom === true;
  }

  get roomList() {
    return this.loginService.rooms || [];
  }

  get doctorList() {
    return this.loginService.clinicUsers || [];
  }

  updateGroupSelection(index: number, newSelection: number[]) {
    const group = this.groupSelections[index];
    group.ids = newSelection;

    if (group.AutoIndex > 0) {
      this.settingsService
        .updateMultiGroup(group.AutoIndex, newSelection, this.isByRooms)
        .then(() => {
          console.log(`Group ${group.AutoIndex} updated`);
        })
        .catch((err) => {
          console.error(`Error updating group ${group.AutoIndex}`, err);
        });
    }
  }

  addNewGroup() {
    this.settingsService
      .createMultiGroup(this.isByRooms)
      .then((newGroup) => {
        const ids: number[] = [];
        const isDefault = this.groupSelections.length === 0;

        this.groupSelections.push({
          AutoIndex: newGroup.AutoIndex,
          ids,
          isDefault,
        });

        this.selectedGroupIndex = this.groupSelections.length - 1;

        if (isDefault) {
          this.settingsService.setDefaultMultiGroup(newGroup.AutoIndex).catch((err) => {
            console.error("Error setting initial default group:", err);
          });
        }
      })
      .catch((err) => {
        console.error("Error adding new group:", err);
      });
  }

  onDefaultGroupChange() {
    const selectedGroup = this.groupSelections[this.selectedGroupIndex];

    if (!selectedGroup?.AutoIndex) {
      console.warn("Trying to set default on unsaved group");
      return;
    }

    this.settingsService
      .setDefaultMultiGroup(selectedGroup.AutoIndex)
      .then(() => {
        // Update local state too
        this.groupSelections.forEach((g, i) => {
          g.isDefault = i === this.selectedGroupIndex;
        });
        console.log("Default group updated successfully");
      })
      .catch((err) => {
        console.error("Error updating default group:", err);
      });
  }

  deleteGroup(index: number) {
    const group = this.groupSelections[index];

    if (group.AutoIndex > 0) {
      this.settingsService
        .deleteMultiGroup(group.AutoIndex)
        .then(() => {
          this.groupSelections.splice(index, 1);
          if (this.selectedGroupIndex >= this.groupSelections.length) {
            this.selectedGroupIndex = this.groupSelections.length - 1;
          }
        })
        .catch((err) => {
          console.error("Error deleting group:", err);
        });
    } else {
      // Just remove from UI if not yet saved
      this.groupSelections.splice(index, 1);
    }
  }

  close() {
    this.closed.emit();
  }
}
